import React, { useEffect, useState, useContext, useRef } from "react";
import { useLocation } from "react-router-dom";

import { MyContext, ListContext } from "./MyContext";
import COLORS from "./styleConstants";
import "./elementsStyle.css";
import { Square, CheckSquare } from "./icons";
import Payment from "./payment";
import CheckoutModal, { Succeeded, PaymentButtons } from "./checkoutModal";

import Auth from "./Auth";
import { getPaymentErrorMessage } from "./paymentErrorMessages";

// Re-exported for backward compatibility (paymentForm.js imports these from here).
export { Succeeded, PaymentButtons };

export const CheckoutSavedPaymentForm = ({ currentPage = "purchases" }) => {
  const { listState, dispatch2 } = useContext(ListContext);
  const { mystate, dispatch } = useContext(MyContext);
  const [paymentMethodsList, setpaymentMethodsList] = useState([]);
  const [paymentMethodId, setPaymentMethodId] = useState();
  const [useNewCard, setUseNewCard] = useState(false);
  const location = useLocation();
  const intentRef = useRef(listState.paymentIntent);
  const submittingRef = useRef(false);

  const ensurePaymentIntent = async () => {
    if (intentRef.current?.id) return intentRef.current;
    const params = location.state?.pendingPaymentParams;
    if (!params) throw new Error("Please restart checkout.");
    const result = await Auth.api(
      "payments/createPaymentIntentAndCustomer/",
      "post",
      params,
      { skipGlobalErrorHandler: true },
    );
    const intent = result.data?.payment_intent;
    if (!intent?.id || !intent.client_secret) {
      throw new Error("Unable to start payment.");
    }
    intentRef.current = intent;
    dispatch2(["SET_PAYMENT_INTENT", intent]);
    return intent;
  };

  useEffect(() => {
    const methods = Array.isArray(listState.paymentMethodList)
      ? listState.paymentMethodList
      : [];
    setpaymentMethodsList(methods);
    setPaymentMethodId((current) =>
      methods.some((method) => method.id === current)
        ? current
        : methods[0]?.id,
    );
  }, [listState.paymentMethodList]);

  // Switch to the new-card form (also used when a saved card needs authentication).
  if (useNewCard) return <Payment currentPage={currentPage} />;

  return (
    <CheckoutModal currentPage={currentPage}>
      {({ setProcessing, setError, onFulfilled, submitRef }) => {
        const enterNewCard = () => {
          if (submittingRef.current) return;
          setUseNewCard(true);
        };
        const handleSubmit = async (ev) => {
          ev.preventDefault();
          if (submittingRef.current || !paymentMethodId) return;
          submittingRef.current = true;

          dispatch({ type: "FETCH_INIT" });
          setProcessing(true);
          setError(null);

          try {
            const intent = await ensurePaymentIntent();
            const params = {
              payment_intent_id: intent.id,
              payment_method_id: paymentMethodId,
            };
            const result = await Auth.api(
              "payments/confirmPaymentIntent",
              "post",
              params,
              { skipGlobalErrorHandler: true },
            );
            const status = result.data?.status;

            if (status === "fulfilled" && result.data.payment_intent_id) {
              onFulfilled();
            } else if (status === "requires_action") {
              alert(
                "Your saved card requires further authentication. Please enter your card number manually.",
              );
              setProcessing(false);
              setUseNewCard(true);
              return;
            } else if (status === "failed" || status === "canceled") {
              const hasDeclineDetails =
                result.data.error_type === "card_error" ||
                Boolean(result.data.decline_code);
              setError(
                getPaymentErrorMessage(
                  {
                    response: {
                      status: hasDeclineDetails ? 402 : 200,
                      data: result.data,
                    },
                  },
                  {
                    staff: ["ADM", "INS", "SPR"].includes(Auth.getUserType()),
                  },
                ),
              );
              setProcessing(false);
              return;
            } else {
              setError(
                "Payment fulfillment was not confirmed. Check the payment status before submitting again.",
              );
              setProcessing(false);
              dispatch({ type: "FETCH_ERROR" });
            }
          } catch (err) {
            dispatch({ type: "FETCH_ERROR" });
            setProcessing(false);

            setError(
              getPaymentErrorMessage(err, {
                staff: ["ADM", "INS", "SPR"].includes(Auth.getUserType()),
              }),
            );
          } finally {
            submittingRef.current = false;
            setProcessing(false);
            dispatch({ type: "FETCH_SUCCESS" });
          }
        };

        // Register this form's handler so the wrapper's pay button triggers it.
        submitRef.current = handleSubmit;

        return (
          <>
            <div
              className="sr-combo-inputs"
              style={{
                border: "solid 1 grey",
                width: "100%",
                marginBottom: 20,
              }}
            >
              {paymentMethodsList.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flex: "1 1 100%",
                    flexDirection: "column",
                  }}
                >
                  {paymentMethodsList.map((pm) => (
                    <div
                      key={pm.id}
                      style={{
                        margin: 0,
                        padding: 10,
                        fontSize: 14,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <div
                        onClick={() => setPaymentMethodId(pm.id)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginRight: 10,
                          fontSize: 20,
                        }}
                      >
                        <div>
                          {paymentMethodId == pm.id ? (
                            <CheckSquare fontSize={30} />
                          ) : (
                            <Square fontSize={30} />
                          )}
                        </div>
                        <div
                          style={{
                            paddingLeft: 5,
                            fontSize: 14,
                            color: COLORS.textDark,
                          }}
                        >
                          {`Use card ending in ${pm.last4}`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div
                style={{
                  margin: 0,
                  padding: 10,
                  fontSize: 14,
                  display: "flex",
                  alignItems: "center",
                  flex: "1 1 100%",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{ fontSize: 20, marginBottom: 5 }}
                  onClick={enterNewCard}
                >
                  {"OR"}
                </div>
                <div style={{ fontSize: 20 }}>
                  <button
                    type="button"
                    onClick={enterNewCard}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: COLORS.orange,
                      cursor: "pointer",
                    }}
                  >
                    Enter a New Card
                  </button>
                </div>
              </div>
            </div>
          </>
        );
      }}
    </CheckoutModal>
  );
};

export default CheckoutSavedPaymentForm;
